import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import MainLayout from './layouts/MainLayout';
import DashboardPage from './pages/DashboardPage';
import MembersPage from './pages/MembersPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import MemberDetailPage from './pages/MemberDetailPage';
import NoticesPage from './pages/NoticesPage';
import FaqsPage from './pages/FaqsPage';
import SettingsPage from './pages/SettingsPage';
import BannersPage from './pages/BannersPage';
import VersionPage from './pages/VersionPage';
import CameraPage from './pages/CameraPage';
import LprTestPage from './pages/LprTestPage';
import AdminsPage from './pages/AdminsPage';
import LoginPage from './pages/LoginPage';
import TwoFactorSetupPage from './pages/TwoFactorSetupPage';
import SalesPage from './pages/SalesPage';
import UsagePage from './pages/UsagePage';
import LprLogPage from './pages/LprLogPage';
import KioskPage from './pages/KioskPage';
import ModernWashPage from './pages/ModernWashPage';
import ModernWashWashStatusPage from './pages/ModernWashWashStatusPage';
import VendorVehicleListPage from './pages/modernwash/VendorVehicleListPage';
import VendorWashLogPage from './pages/modernwash/VendorWashLogPage';
import VendorPaymentListPage from './pages/modernwash/VendorPaymentListPage';
import OwnerConsoleApp from './owner-console-v2/OwnerConsoleApp';
import PlatformAdminPortal from './platform-admin/PlatformAdminPortal';
import {
  ModernWashOperationsMemberDetail,
  ModernWashOperationsMembers,
  ModernWashOperationsPortal,
} from './modernwash-operations';
import { getRouterBasename } from './api/config';
import { adminLandingPath, isStoreOperatorRole } from './utils/adminLanding';
import { getAdminRole, getAdminStoreCode } from './utils/auth';

const theme = createTheme({
  typography: {
    fontFamily: '"Pretendard", "Noto Sans KR", sans-serif',
    h4: { fontWeight: 800, letterSpacing: '-0.02em' },
    h5: { fontWeight: 800, letterSpacing: '-0.02em' },
    h6: { fontWeight: 700, letterSpacing: '-0.01em' },
    button: { fontWeight: 700, letterSpacing: 0 },
  },
  palette: {
    primary: { main: '#1976d2', dark: '#0d47a1' },
    secondary: { main: '#E91E63' },
    background: {
      default: '#f5f6fa', // 조금 더 부드러운 회색
      paper: '#ffffff',
    },
    divider: 'rgba(0,0,0,0.06)',
  },
  // surface 기본값. 조작 control은 아래 override에서 8px로 명시한다.
  shape: { borderRadius: 12 },
  components: {
    // ── 한글 줄바꿈 전역 정책 ─────────────────────────
    // word-break: keep-all → 단어 중간(자음/모음)에서 줄바꿈 안 됨
    // 예: "가장 한산한 요일" → "요\n일" 깨짐 방지
    // overflow-wrap: anywhere → 너무 긴 단어는 어쩔 수 없이 끊김 (이메일/URL)
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          wordBreak: 'keep-all',
          overflowWrap: 'anywhere',
        },
      },
    },
    // 카드 (Paper) — 더 부드러운 그림자 + hover lift
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid rgba(0,0,0,0.06)',
        },
      },
    },
    // 버튼 — 깔끔한 radius + 그림자 제거
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 36,
          borderRadius: '8px',
          padding: '8px 14px',
          textTransform: 'none',
          lineHeight: 1.2,
          textAlign: 'center',
        },
        sizeSmall: { minHeight: 32, padding: '6px 10px', lineHeight: 1.2 },
        contained: {
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          '&:hover': { boxShadow: '0 4px 12px rgba(25,118,210,0.18)' },
        },
      },
    },
    // 버튼·행·메뉴의 hover 면적과 control 반경을 하나의 규격으로 고정한다.
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: '8px',
          transition: 'background-color 120ms ease, color 120ms ease',
          '&:hover': { backgroundColor: 'rgba(15, 23, 42, 0.045)' },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 36,
          borderRadius: '8px',
          margin: '2px 4px',
          '&:hover': { backgroundColor: 'rgba(15, 23, 42, 0.045)' },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '&:hover': { backgroundColor: 'rgba(15, 23, 42, 0.045)' },
        },
      },
    },
    // 입력 필드 — 라운드
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: '8px',
          '&.MuiInputBase-multiline': { minHeight: 0, alignItems: 'flex-start' },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          minHeight: 36,
          padding: '0 12px',
          lineHeight: 1.2,
          textAlign: 'center',
          whiteSpace: 'nowrap',
        },
        sizeSmall: { minHeight: 32, padding: '0 10px' },
      },
    },
    // 테이블 — 헤더 강조
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 700,
            backgroundColor: '#fafbfd',
            color: '#374151',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { padding: '10px 16px', verticalAlign: 'middle' },
        sizeSmall: { padding: '8px 16px' },
        head: { height: 44, paddingTop: 0, paddingBottom: 0 },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover > .MuiTableCell-root': {
            backgroundColor: 'rgba(15, 23, 42, 0.035)',
          },
        },
      },
    },
    // 다이얼로그 — 둥근 모서리
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16 },
      },
    },
    // 칩 — 좀 더 modern
    MuiChip: {
      styleOverrides: {
        root: {
          height: 24,
          boxSizing: 'border-box',
          fontWeight: 600,
          '& .MuiChip-label': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            lineHeight: 1,
          },
        },
        // 표와 카드에서 작은 Chip도 같은 24px control height를 쓴다.
        // 22px만 쓰면 행의 세로 정렬이 흔들리고 label의 위아래 여백도 제각각으로 보인다.
        sizeSmall: { height: 24 },
      },
    },
  },
});

// 로그인 여부 확인
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" />;
  return children;
};

// 이미 인증된 관리자가 로그인 화면으로 돌아오면 빈 화면에 머무르지 않고
// 기본 관리자 홈으로 보낸다. 토큰이 만료된 경우 실제 API 요청은 기존처럼
// client interceptor가 토큰을 지우고 /login으로 돌린다.
const GuestOnlyRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (token) return <Navigate to={adminLandingPath(getAdminRole())} replace />;
  return children;
};

/** 전주점 전용 도메인의 루트는 운영 콘솔로 고정한다. */
const isModernWashOperationsHost = () =>
  typeof window !== 'undefined' && window.location.hostname === 'jj001.mogoo.co.kr';

/** 지점 계정이 예전 '/' 주소로 들어와도 점주 콘솔로 보낸다. */
const AdminHomeRoute = () => {
  const role = getAdminRole();
  const canOperateModernWash = role === 'master' || (role === 'manager' && getAdminStoreCode() === 'jeonju');

  if (isModernWashOperationsHost() && canOperateModernWash) {
    return <Navigate to="/modernwash-ops" replace />;
  }
  return isStoreOperatorRole(role) ? <Navigate to="/owner-v2" replace /> : <DashboardPage />;
};

/**
 * 플랫폼 관리 포털은 master 계정의 업무 시작점이다.
 * 실제 데이터 권한은 각 백엔드 API가 다시 검증하며, 이 경계는 화면 진입 UX만 담당한다.
 */
const PlatformAdminRoute = ({ children }) =>
  getAdminRole() === 'master' ? children : <Navigate to={adminLandingPath(getAdminRole())} replace />;

/**
 * ModernWash 전주점 운영 포털은 플랫폼 master 또는 해당 지점 manager만 연다.
 * V1/JJ001 원천의 접근 통제는 서버에서도 별도로 유지해야 한다.
 */
const ModernWashOperationsRoute = ({ children }) => {
  const role = getAdminRole();
  const isAllowed = role === 'master' || (role === 'manager' && getAdminStoreCode() === 'jeonju');
  return isAllowed ? children : <Navigate to={adminLandingPath(role)} replace />;
};

/**
 * 점주 콘솔의 사람이 읽기 쉬운 공식 별칭.
 * 기존 /owner-v2 링크와 구현을 유지하면서 하위 경로·쿼리까지 손실 없이 넘긴다.
 */
const OwnerConsoleAliasRoute = () => {
  const location = useLocation();
  const ownerSuffix = location.pathname.replace(/^\/owner(?=\/|$)/, '');
  return <Navigate to={`/owner-v2${ownerSuffix}${location.search}${location.hash}`} replace />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter basename={getRouterBasename()}>
        <Routes>
          <Route
            path="/login"
            element={
              <GuestOnlyRoute>
                <LoginPage />
              </GuestOnlyRoute>
            }
          />
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<AdminHomeRoute />} />
            <Route path="/members" element={<MembersPage />} />
            <Route path="/members/:memberId" element={<MemberDetailPage />} />
            <Route path="/subscriptions" element={<SubscriptionsPage />} />
            <Route path="/notices" element={<NoticesPage />} />
            <Route path="/faqs" element={<FaqsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/banners" element={<BannersPage />} />
            <Route path="/versions" element={<VersionPage />} />
            <Route path="/camera" element={<CameraPage />} />
            <Route path="/lpr-test" element={<LprTestPage />} />
            <Route path="/admins" element={<AdminsPage />} />
            <Route path="/2fa-setup" element={<TwoFactorSetupPage />} />
            <Route path="/sales" element={<SalesPage />} />
            <Route path="/usage" element={<UsagePage />} />
            <Route path="/lpr-logs" element={<LprLogPage />} />
            <Route path="/kiosk" element={<KioskPage />} />
            <Route path="/modernwash" element={<ModernWashPage />} />
            <Route path="/modernwash/daily" element={<ModernWashWashStatusPage variant="single" />} />
            <Route path="/modernwash/range" element={<ModernWashWashStatusPage variant="range" />} />
            <Route path="/modernwash/vehicle-list" element={<VendorVehicleListPage />} />
            <Route path="/modernwash/wash-log" element={<VendorWashLogPage />} />
            <Route path="/modernwash/payment-list" element={<VendorPaymentListPage />} />
          </Route>
          {/* 플랫폼 운영자 전용: 기존 MainLayout과 분리한 control-plane 시작 화면 */}
          <Route
            path="/platform/*"
            element={
              <ProtectedRoute>
                <PlatformAdminRoute>
                  <PlatformAdminPortal />
                </PlatformAdminRoute>
              </ProtectedRoute>
            }
          />
          {/* ModernWash 전주점 전용: V1/JJ001을 읽기 전용 운영 근거로 구분한 화면 */}
          <Route
            path="/modernwash-ops"
            element={
              <ProtectedRoute>
                <ModernWashOperationsRoute>
                  <ModernWashOperationsPortal />
                </ModernWashOperationsRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modernwash-ops/members"
            element={
              <ProtectedRoute>
                <ModernWashOperationsRoute>
                  <ModernWashOperationsMembers />
                </ModernWashOperationsRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modernwash-ops/members/:memberId"
            element={
              <ProtectedRoute>
                <ModernWashOperationsRoute>
                  <ModernWashOperationsMemberDetail />
                </ModernWashOperationsRoute>
              </ProtectedRoute>
            }
          />
          {/* 공식 점주 콘솔 별칭. 실제 구현은 한 곳(/owner-v2)만 유지한다. */}
          <Route path="/owner/*" element={<OwnerConsoleAliasRoute />} />
          {/* 신규 점주 콘솔 v2 — 기존 관리자와 분리된 병렬 구축 (운영 기본 진입점 아님)
              미로그인은 기존 정책대로 /login, 세션/권한은 backend GET /owner-console/session 기준 */}
          <Route
            path="/owner-v2/*"
            element={
              <ProtectedRoute>
                <OwnerConsoleApp />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
